import React from 'react';
import { performanceTierStacks } from 'legacy-src/utility/progressHelpers';
import { isEmpty, cloneDeep } from 'lodash';
import uniqid from 'uniqid';
import PerformanceBar from 'legacy-src/components/assessment/Results/PerformanceBar';
import PerformanceBarPopover from 'legacy-src/components/assessment/Results/PerformanceBarPopover';
import ScoredIcon from 'chameleon/ui-stack/ui/assessments/scored-icon';
import SPPProgressBarPopover from 'chameleon/ui-stack/ui/assessments/spp-progress-bar';
import {
  assignmentStartDate,
  assignmentDueDate,
  dateIsSet,
} from 'legacy-src/utility/assignmentDates';
import { COURSE_GUIDE_LABEL } from 'chameleon-src/components/SidebarWrapperTwoLevels/constants';
import usePerformanceTiers from 'chameleon/src/hooks/usePerformanceTiers';

export const combineTeacherAndStudentScores = (
  teacherScores,
  studentScores,
) => {
  if (!studentScores.length) {
    return teacherScores;
  }
  if (!teacherScores.length) {
    return studentScores;
  }

  const scoringSessionId = Object.keys(teacherScores[0].data)[0];
  const teacherScoredStudents = !isEmpty(teacherScores[0].data)
    ? Object.values(teacherScores[0].data)[0].users
    : [];
  const selfScoredStudents = !isEmpty(studentScores[0].data)
    ? Object.values(studentScores[0].data)[0].users
    : [];
  const combinedScores = { ...teacherScoredStudents };

  Object.keys(selfScoredStudents).forEach((id) => {
    if (!teacherScoredStudents[id]) {
      combinedScores[id] = selfScoredStudents[id];
    }
  });

  const combinedResult = cloneDeep(teacherScores);
  if (!isEmpty(combinedResult[0].data[scoringSessionId])) {
    combinedResult[0].data[scoringSessionId].users = combinedScores;
  }

  return combinedResult;
};

export const renderPerformanceBar = ({
  params,
  results,
  assignment,
  students,
  showScoreIcon = true,
  performancePopoverProps = {},
  hideTooltip = false,
}) => {
  const onNoResultsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const subjectId = params.subject;

  const { performanceTiers } = usePerformanceTiers(subjectId);
  const assignmentId = params.assignmentId || assignment.id;
  const baseLink = `/${subjectId}/assessments/results/${assignmentId}`;
  const studentUrl = `${baseLink}/students`;
  const reports = results;
  const { progress = {}, assessment, studentSessions } = assignment || {};
  const { isSpp } = assessment || {};

  if (isSpp) {
    return (
      <SPPProgressBarPopover
        isUnassigned={false}
        assignment={{
          ...assignment,
          report: results[0],
          students_who_submitted_spp: studentSessions
            .filter((student) => !!student.submittedAt)
            .map((student) => parseInt(student.userId, 10)),
        }}
        students={students.map((student) => ({
          ...student,
          id: parseInt(student.id, 10),
        }))}
        fullWidth
        hideTooltip={hideTooltip}
      />
    );
  }

  const stacks = performanceTierStacks(
    null,
    reports,
    performanceTiers,
    students,
  );

  const itemTitle = (assignment && assignment.title) || '';
  const ariaLabel = `Open student performance results: ${stacks.reduce(
    (a, b) => `${a}, ${b.label}: ${b.value}`,
    itemTitle,
  )}`;
  const popOverContentId = `student-performance-popover-${uniqid()}`;
  const { scored, studentScored, teacherAndStudentScored } = progress;

  return (
    <div className="result_page_student_performance_container">
      {isEmpty(reports) ? (
        <div onClick={onNoResultsClick}>
          <PerformanceBar
            isSmall
            stacks={[
              {
                label: 'No Results',
                text: 'No Results',
                value: -1,
                status: '--no-results',
              },
            ]}
          />
        </div>
      ) : (
        <PerformanceBarPopover
          stacks={stacks}
          ariaDescribedBy={popOverContentId}
          ariaLabel={ariaLabel}
          perfDetailsTitle={null}
          contentOffset="0 -2rem 0 0"
          studentUrl={studentUrl}
          disableNoResultsLink={false}
          disabled={reports.length === 0}
          fullWidth
          {...performancePopoverProps}
        />
      )}

      {showScoreIcon && (
        <ScoredIcon
          teacherScored={scored}
          studentScored={studentScored}
          teacherAndStudentScored={teacherAndStudentScored}
        />
      )}
    </div>
  );
};

export const StartDueLabels = ({ studentId, assignment }) => {
  let customAssignmentSetting = {};
  if (studentId) {
    customAssignmentSetting = (assignment?.customAssignmentSettings || []).find(
      (setting) => setting?.userId === Number.parseInt(studentId, 10),
    );
  }

  /**
   * If a custom assignment setting exists, in particular when a teacher
   * is looking at a student's results, we should use the dates in the CAS
   */
  const startDate = assignmentStartDate(
    { ...assignment, ...customAssignmentSetting },
    'dayMonthTime',
  );
  const dueDate = assignmentDueDate(
    { ...assignment, ...customAssignmentSetting },
    'dayMonthTime',
  );

  const renderDateValue = (date, label) => (
    <span
      className={`mobile:inline-block mobile:w-80 ${label}_at`}
      {...(!dateIsSet(date) && { role: 'img', 'aria-label': 'Not Set' })}
    >
      {date}
    </span>
  );

  return (
    <React.Fragment>
      <span>
        <label className="font-bold mr-0.8 text-14 text-gray-600 tracking-wide">
          START
        </label>
        {renderDateValue(startDate, 'starts')}
      </span>
      <span>
        <label className="font-bold mr-0.8 text-14 text-gray-600 tracking-wide">
          DUE
        </label>
        {renderDateValue(dueDate, 'due')}
      </span>
    </React.Fragment>
  );
};

export const pathBreadCrumbMapping = {
  'results-summary': 'Results Overview',
  ClassPerformanceItem: 'Question Details',
  ClassPerformanceItemPart: 'Question Details',
  SingleStudentPerformanceItem: 'Question Details',
  SingleStudentPerformanceItemPart: 'Question Details',
  SingleStudentPerformanceItemOption: 'Question Details',
  SingleStudentResources: 'Student Results',
  SingleStudentQuestions: 'Student Results',
  SingleStudentPartOverview: 'Student Results',
  SingleStudentPerformanceOverview: 'Student Results',
  StudentRubricsQuestions: 'Student Responses',
  SingleStudentRubricsQuestions: 'Your Responses',
  AssignmentProgress: 'Assignment Overview',
};

export const getDefaultBreadcrumb = (subject) => ({
  text: COURSE_GUIDE_LABEL,
  to: `/${subject}/home`,
});
